// One agent's card (SCR-576): its name and what it covers, its effective list as chips (the
// default marked), a Default model select whose options are exactly that list, and "Narrow for
// this agent": the agent's own providers and allow list, bounded by the global list (an option
// the org does not allow is greyed, "not allowed globally"). Display names sit under "Advanced"
// (REQ-239). The card edits the draft; the section's Save writes it.
import { errorId, Field, Fieldset } from "../../primitives/field";
import type { HelpId } from "../../primitives/help";
import { Card, Chip } from "../../primitives/page";
import { FieldRenderer } from "../../settings/field-renderer";
import { getPath, type Json } from "../../settings/inherit";
import { fieldMode, pinFor, type FieldMode, type Pins } from "../../settings/pins";
import { toggleModel, toggleProvider } from "../../settings/pickers";
import { FIELDS, type FieldDescriptor } from "../../settings/validate";
import type { AgentCardModel, AgentErrors, NarrowChoices } from "./agents-model";
import { fieldPath, policyOf, type ModelField } from "./model";
import type { Surface } from "../../api/org-context";

/** What the Advanced disclosure says about display names (REQ-239). */
export const DISPLAY_NAMES_NOTE = "The name Claude Code, Codex or the chat shows in its model picker. Requests still go to the same model.";

export interface AgentCardProps {
  card: AgentCardModel;
  narrow: NarrowChoices;
  /** The effective document the card reads its policy from (the draft over what it inherits). */
  effective: Json | undefined;
  /** Fields the edited layer does not set: shown as inherited. */
  inherited: (path: string) => boolean;
  pins: Pins;
  surface: Surface;
  /** The server's refusals for this agent, placed on the select and the narrowing picker. */
  errors: AgentErrors;
  /** Client-side schema errors by field path. */
  fieldErrors: Readonly<Record<string, string>>;
  onChange(path: string, value: Json | undefined): void;
}

function descriptor(path: string): FieldDescriptor {
  const found = FIELDS.find((f) => f.path === path);
  if (!found) throw new Error(`settings field ${path} is not in the schema`);
  return found;
}

export function AgentCard({ card, narrow, effective, inherited, pins, surface, errors, fieldErrors, onChange }: AgentCardProps) {
  const { harness } = card;
  const policy = policyOf(effective, harness);
  const modeOf = (name: ModelField): FieldMode => fieldMode(descriptor(fieldPath(harness, name)), surface, pinFor(pins, fieldPath(harness, name)));
  const locked = (name: ModelField) => modeOf(name).kind === "readonly";
  const note = (name: ModelField) => {
    const mode = modeOf(name);
    const parts = [mode.kind === "readonly" ? mode.note : "", inherited(fieldPath(harness, name)) ? "Inherited from the org default." : ""];
    return parts.filter(Boolean).join(" ") || undefined;
  };
  const selectId = `agent-${harness.key}-default`;
  const defaultError = errors.default ?? fieldErrors[fieldPath(harness, "default")];
  const narrowError = errors.narrow ?? fieldErrors[fieldPath(harness, "allowed")] ?? fieldErrors[fieldPath(harness, "providers")];
  const render = (name: "mode" | "include_new_models" | "display_names") => {
    const field = descriptor(fieldPath(harness, name));
    return <FieldRenderer field={field} value={getPath(effective, field.path)} mode={modeOf(name)} inherited={inherited(field.path)} error={fieldErrors[field.path]} onChange={(next) => onChange(field.path, next)} />;
  };

  return (
    <Card title={card.label} class="harness-card agent-card">
      <p class="card-note">{card.blurb}</p>
      {card.pending ? (
        <p class="field-hint" role="status">
          The proxy has not reported its models yet, so the models this agent is offered are not known.
        </p>
      ) : card.effective.length === 0 ? (
        <p class="field-hint" role="status">
          No model is allowed for this agent. Allow one on Families.
        </p>
      ) : (
        <ul class="chip-list" aria-label={`Models ${card.label} is offered`}>
          {card.effective.map((m) => (
            <li key={m.model}>
              <Chip tone={m.invokable ? (m.model === card.default?.model ? "blue" : "grey") : "amber"} title={m.invokable ? undefined : "Not invokable in this region"}>
                {m.displayName}
                {m.model === card.default?.model ? " · default" : ""}
              </Chip>
            </li>
          ))}
        </ul>
      )}
      <Field id={selectId} label="Default model" help={descriptor(fieldPath(harness, "default")).help as HelpId} hint={note("default")} error={defaultError}>
        <select id={selectId} value={card.default?.model ?? card.pending?.model ?? ""} disabled={locked("default") || card.effective.length === 0} aria-invalid={defaultError ? true : undefined} aria-describedby={defaultError ? errorId(selectId) : undefined} onChange={(event) => onChange(fieldPath(harness, "default"), event.currentTarget.value)}>
          {card.pending && <option value={card.pending.model}>{card.pending.displayName}</option>}
          {card.default === null && !card.pending && <option value="">{card.unavailableDefault ? `${card.unavailableDefault} (not allowed; choose another)` : "Choose a model"}</option>}
          {card.effective.map((m) => (
            <option key={m.model} value={m.model}>
              {m.displayName}
            </option>
          ))}
        </select>
      </Field>
      {card.unavailableDefault && (
        <p class="field-hint" role="status">
          <Chip tone="amber">default unavailable</Chip> {card.unavailableDefault} is no longer allowed for {card.label}. Choose another default and save.
        </p>
      )}
      {render("include_new_models")}
      <details class="agent-narrow" open={narrowError !== undefined}>
        <summary>Narrow for this agent</summary>
        <div class="stack-sm">
          {render("mode")}
          <Fieldset label="Providers for this agent" help={descriptor(fieldPath(harness, "providers")).help as HelpId}>
            {note("providers") && <p class="field-hint">{note("providers")}</p>}
            <div class="picker-grid">
              {narrow.providers.map((p) => (
                // help-exempt: the group's legend carries the help button.
                <label key={p.provider} class={p.allowedGlobally ? "picker-option" : "picker-option picker-option-greyed"}>
                  <input type="checkbox" checked={p.checked} disabled={locked("providers") || (!p.allowedGlobally && !p.checked)} onChange={(event) => onChange(fieldPath(harness, "providers"), toggleProvider(policy.providers, p.provider, event.currentTarget.checked))} />
                  {p.label}
                  {!p.allowedGlobally && <span class="field-hint">not allowed globally</span>}
                </label>
              ))}
            </div>
          </Fieldset>
          {policy.mode === "allow_list" && (
            <Fieldset label="Allowed models" help={descriptor(fieldPath(harness, "allowed")).help as HelpId} error={narrowError}>
              {note("allowed") && <p class="field-hint">{note("allowed")}</p>}
              <ul class="model-checks">
                {narrow.models.map((m) => (
                  <li key={m.model} class={m.allowedGlobally ? "model-check" : "model-check model-check-greyed"}>
                    {/* help-exempt: the group's legend carries the help button. */}
                    <label>
                      <input type="checkbox" checked={m.checked} disabled={locked("allowed") || (!m.allowedGlobally && !m.checked)} onChange={(event) => onChange(fieldPath(harness, "allowed"), toggleModel(policy.allowed, m.model, event.currentTarget.checked))} />
                      <span class="model-check-name">
                        {m.displayName}
                        <span class="cell-sub mono">{m.model}</span>
                      </span>
                    </label>
                    {!m.allowedGlobally && <Chip tone="grey">not allowed globally</Chip>}
                  </li>
                ))}
              </ul>
            </Fieldset>
          )}
          {policy.mode !== "allow_list" && narrowError && (
            <p class="field-error" role="alert">
              {narrowError}
            </p>
          )}
        </div>
      </details>
      <details class="agent-narrow agent-advanced">
        <summary>Advanced</summary>
        <p class="card-note">{DISPLAY_NAMES_NOTE}</p>
        {render("display_names")}
      </details>
    </Card>
  );
}
